import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ComponentPermissionService, PermissionContext } from './component-permission.service';
import { AuthService } from '../auth/auth.service';
import { ActivatedRoute } from '@angular/router';

interface PermissionVisibilityEntry {
  ready: boolean;
  visible: boolean;
}

interface PendingPermissionCheck {
  component: any;
  modalData?: any;
  route?: ActivatedRoute;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionEvaluatorService {
  private permissionVisibilityCache = new Map<string, PermissionVisibilityEntry>();
  private readonly permissionVisibilityChanged$ = new Subject<string>();
  private readonly pendingChecks = new Map<string, PendingPermissionCheck>();
  private readonly inFlightChecks = new Set<string>();
  private authStateReady = false;

  constructor(
    private componentPermissionService: ComponentPermissionService,
    private authService: AuthService
  ) {
    this.authService.authStateReady$.subscribe((ready) => {
      if (ready && !this.authStateReady) {
        this.authStateReady = true;
        this.reEvaluatePendingPermissions();
      }
    });
  }

  /** Emits component UUID when async permission visibility has been resolved. */
  onPermissionVisibilityChanged(): Observable<string> {
    return this.permissionVisibilityChanged$.asObservable();
  }

  clearPermissionVisibilityCache(): void {
    this.permissionVisibilityCache.clear();
    if (this.authStateReady) {
      this.reEvaluatePendingPermissions();
    }
  }

  coerceBoolean(value: unknown): boolean {
    if (value === true || value === 1) return true;
    if (value === false || value === 0 || value == null) return false;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }
    return Boolean(value);
  }

  needsPermissionCheck(component: any): boolean {
    const enabled = this.coerceBoolean(component?.settings?.enablePermissionCheck?.value);
    const permissionFunction = String(component?.settings?.permissionFunction?.value ?? '').trim();
    return enabled && permissionFunction.length > 0;
  }

  /**
   * Starts async permission evaluation for a component (no-op when not configured).
   * Call from synchronous visibility checks; subscribe to onPermissionVisibilityChanged() to refresh UI.
   */
  ensurePermissionChecked(component: any, modalData?: any, route?: ActivatedRoute): void {
    const uuid = component?.uuid;
    if (!uuid || !this.needsPermissionCheck(component)) {
      return;
    }

    this.pendingChecks.set(uuid, { component, modalData, route });

    const cached = this.permissionVisibilityCache.get(uuid);
    if (cached?.ready) {
      return;
    }

    if (!this.authStateReady) {
      return;
    }

    this.startPermissionCheck(uuid);
  }

  private startPermissionCheck(uuid: string): void {
    const pending = this.pendingChecks.get(uuid);
    if (!pending || this.inFlightChecks.has(uuid)) {
      return;
    }

    const cached = this.permissionVisibilityCache.get(uuid);
    if (cached?.ready) {
      return;
    }

    this.inFlightChecks.add(uuid);
    this.permissionVisibilityCache.set(uuid, { ready: false, visible: false });

    this.checkComponentVisibility(pending.component, pending.modalData, pending.route).subscribe({
      next: (visible) => {
        this.inFlightChecks.delete(uuid);
        this.permissionVisibilityCache.set(uuid, { ready: true, visible });
        this.permissionVisibilityChanged$.next(uuid);
      },
      error: () => {
        this.inFlightChecks.delete(uuid);
        this.permissionVisibilityCache.set(uuid, { ready: true, visible: false });
        this.permissionVisibilityChanged$.next(uuid);
      }
    });
  }

  private reEvaluatePendingPermissions(): void {
    for (const uuid of this.pendingChecks.keys()) {
      this.permissionVisibilityCache.delete(uuid);
      this.startPermissionCheck(uuid);
    }
  }

  /**
   * Returns cached permission result. When no permission is configured, returns true.
   * While async check is pending, returns false (hide until resolved).
   */
  getCachedPermissionVisible(component: any): boolean {
    if (!this.needsPermissionCheck(component)) {
      return true;
    }

    const cached = this.permissionVisibilityCache.get(component?.uuid);
    if (!cached?.ready) {
      return false;
    }

    return cached.visible;
  }

  /**
   * Evaluates a permission function with context variable substitution
   * @param permissionFunction - The name of the permission function to execute
   * @param contextTemplate - JSON string with placeholder variables
   * @param modalData - Data from the modal or component
   * @param route - Angular ActivatedRoute for route parameters
   * @returns Observable<boolean> indicating if permission is granted
   */
  evaluatePermission(
    permissionFunction: string,
    contextTemplate?: string,
    modalData?: any,
    route?: ActivatedRoute
  ): Observable<boolean> {
    
    // If no permission function specified, allow access
    if (!permissionFunction) {
      return of(true);
    }

    // Build context object by substituting variables in the template
    let context: PermissionContext = {};
    
    if (contextTemplate) {
      try {
        context = this.buildContext(contextTemplate, modalData, route);
      } catch (error) {
        console.error('[PermissionEvaluator] Error building context:', error);
        return of(false);
      }
    }

    // Execute the permission function
    return this.componentPermissionService.executePermission(permissionFunction, context);
  }

  /**
   * Builds the context object by substituting variables in the template
   * @param template - JSON string with placeholder variables
   * @param modalData - Data from the modal
   * @param route - Angular ActivatedRoute
   * @returns PermissionContext object with substituted values
   */
  private buildContext(template: string, modalData?: any, route?: ActivatedRoute): PermissionContext {
    
    // Get current user for substitution
    const currentUser = this.authService.getCurrentUser();
    
    // Get route parameters if route is provided
    const routeParams = route?.snapshot.params || {};
    const queryParams = route?.snapshot.queryParams || {};
    
    // Substitute variables in the template
    let substitutedTemplate = template;
    
    // Replace modalData placeholders
    if (modalData) {
      Object.keys(modalData).forEach(key => {
        const placeholder = `{{modalData.${key}}}`;
        const value = modalData[key];
        substitutedTemplate = substitutedTemplate.replace(
          new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'),
          JSON.stringify(value)
        );
      });
    }
    
    // Replace currentUser placeholders
    if (currentUser) {
      Object.keys(currentUser).forEach(key => {
        const placeholder = `{{currentUser.${key}}}`;
        const value = currentUser[key];
        substitutedTemplate = substitutedTemplate.replace(
          new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'),
          JSON.stringify(value)
        );
      });
    }
    
    // Replace route parameter placeholders
    Object.keys(routeParams).forEach(key => {
      const placeholder = `{{route.params.${key}}}`;
      const value = routeParams[key];
      substitutedTemplate = substitutedTemplate.replace(
        new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'),
        JSON.stringify(value)
      );
    });
    
    // Replace query parameter placeholders
    Object.keys(queryParams).forEach(key => {
      const placeholder = `{{route.query.${key}}}`;
      const value = queryParams[key];
      substitutedTemplate = substitutedTemplate.replace(
        new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'),
        JSON.stringify(value)
      );
    });
    
    
    // Parse the final JSON
    return JSON.parse(substitutedTemplate);
  }

  /**
   * Helper method to check component visibility based on both roles and permissions
   * @param component - The component settings
   * @param modalData - Data from the modal
   * @param route - Angular ActivatedRoute
   * @returns Observable<boolean> indicating if component should be visible
   */
  checkComponentVisibility(
    component: any,
    modalData?: any,
    route?: ActivatedRoute
  ): Observable<boolean> {

    const enablePermissionCheck = this.coerceBoolean(component.settings?.enablePermissionCheck?.value);
    const permissionFunction = String(component.settings?.permissionFunction?.value ?? '').trim();
    const contextTemplate = component.settings?.permissionContextFields?.value;

    if (enablePermissionCheck && permissionFunction) {
      return this.evaluatePermission(permissionFunction, contextTemplate, modalData, route).pipe(
        map((permissionGranted) => permissionGranted && this.passesRoleVisibility(component))
      );
    }

    return of(this.passesRoleVisibility(component));
  }

  private passesRoleVisibility(component: any): boolean {
    const currentUser = this.authService.getCurrentUser();
    const userRoles = currentUser?.roles || [];
    const allowedRoles = component.settings?.allowedRoles?.value || [];

    if (userRoles.includes('superadmin') || userRoles.includes('Superadmin')) {
      return true;
    }

    if (allowedRoles && allowedRoles.length > 0) {
      const hasAllowedRole = allowedRoles.some((role: string) => userRoles.includes(role));
      if (!hasAllowedRole) {
        return false;
      }
    } else {
    }

    return true;
  }
} 